"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLanguage, Language } from '../context/LanguageContext';

// Define the supported languages and their full names - memoized at module level
const SUPPORTED_LANGUAGES: Language[] = ['de', 'en', 'fr', 'es'];
const LANGUAGE_NAMES: Record<string, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
};

// Define flag emojis for each language - memoized at module level
const LANGUAGE_FLAGS: Record<string, string> = {
  de: '🇨🇭',
  en: '🇬🇧',
  fr: '🇨🇭',
  es: '🇪🇸',
};

// Helper function to debounce window resize
function useDebounce(fn: Function, ms: number) {
  const timer = useRef<NodeJS.Timeout>();

  const debouncedFn = useCallback((...args: any[]) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      fn(...args);
    }, ms);
  }, [fn, ms]);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  return debouncedFn;
}

interface LanguageSelectorProps {
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = React.memo(({ className }) => {
  const { language, setLanguage, translate } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Memoized functions
  const checkIfMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  // Debounced resize handler
  const debouncedCheckIfMobile = useDebounce(checkIfMobile, 200);

  // Check if we're on mobile on mount and when window resizes - optimized with debouncing
  useEffect(() => {
    // Initial check without debounce
    checkIfMobile();

    // Add debounced listener for resize events
    window.addEventListener('resize', debouncedCheckIfMobile);

    return () => {
      window.removeEventListener('resize', debouncedCheckIfMobile);
    };
  }, [checkIfMobile, debouncedCheckIfMobile]);

  // Memoized event handlers
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  // Close dropdown when clicking outside - optimized with useCallback
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, handleClickOutside]);

  // Memoized event handlers
  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleLanguageChange = useCallback((lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  }, [setLanguage]);

  // Get the flag emoji for the language - memoized
  const getFlag = useCallback((lang: string): string => {
    return LANGUAGE_FLAGS[lang] || '';
  }, []);

  // Memoized current language info
  const currentFlag = useMemo(() => getFlag(language), [getFlag, language]);
  const currentLanguageName = useMemo(() => LANGUAGE_NAMES[language] || language, [language]);

  // Mobile overlay dropdown
  if (isMobile) {
    return (
      <div
        className={`relative ${className}`}
        ref={dropdownRef}
      >
        <button
          aria-label="Language Selector"
          className="flex items-center gap-1 px-3 py-2 text-white transition-all hover:bg-blue-800 bg-blue-900 rounded-md"
          onClick={toggleDropdown}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span className="text-lg">🌐</span>
          <span className="hidden md:inline">{currentFlag} {currentLanguageName}</span>
          <span className="md:hidden">{currentFlag}</span>
          <span className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {/* Fullscreen mobile dropdown */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-label="Select language"
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-5/6 max-w-xs overflow-hidden animate-fadeIn"
            >
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-medium text-lg text-gray-800">{translate('language.select', 'Selectați limba')}</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Close language selector"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>

              <div className="py-2">
                {SUPPORTED_LANGUAGES.map((lang, index) => (
                  <button
                    key={lang}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors
                      ${language === lang
                        ? 'bg-blue-50 text-blue-800'
                        : 'hover:bg-gray-100 text-gray-800'
                      }
                      ${index > 0 ? 'border-t border-gray-100' : ''}
                    `}
                    onClick={() => handleLanguageChange(lang)}
                    aria-current={language === lang ? 'true' : 'false'}
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animation: 'fadeInUp 300ms ease-out forwards'
                    }}
                  >
                    <span className="text-xl">{getFlag(lang)}</span>
                    <span className="font-medium">{LANGUAGE_NAMES[lang] || lang}</span>
                    {language === lang && (
                      <span className="ml-auto bg-blue-100 text-blue-800 rounded-full px-2 py-1 text-xs">
                        Active
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop dropdown
  return (
    <div
      className={`relative ${className}`}
      ref={dropdownRef}
    >
      <button
        aria-label="Language Selector"
        className="flex items-center gap-1 px-3 py-2 text-white transition-all hover:bg-blue-800 rounded-md"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-lg">🌐</span>
        <span>{currentFlag} {currentLanguageName}</span>
        <span className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div
          className="absolute top-full right-0 mt-1 bg-white rounded shadow-lg border border-gray-100 z-50 w-48 py-1 overflow-hidden"
          role="menu"
        >
          {SUPPORTED_LANGUAGES.map((lang, index) => (
            <button
              key={lang}
              className={`w-full text-left pl-3 pr-4 py-2 flex items-center gap-2 transition-colors
                ${language === lang
                  ? 'bg-blue-50 text-blue-800 font-medium'
                  : 'hover:bg-gray-100 text-gray-800'
                }
              `}
              onClick={() => handleLanguageChange(lang)}
              role="menuitem"
              aria-current={language === lang ? 'true' : 'false'}
              style={{
                animationDelay: `${index * 50}ms`,
                animation: 'fadeInDown 200ms ease-out forwards'
              }}
            >
              <span className="text-xl">{getFlag(lang)}</span>
              <span>{LANGUAGE_NAMES[lang] || lang}</span>
            </button>
          ))}
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
});

// Add display name for debugging
LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;